import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import {
  AlertTriangle,
  ArrowLeft,
  Calendar,
  CheckCircle2,
  Clock,
  Copy,
  Edit,
  ExternalLink,
  Eye,
  Info,
  Loader2,
  Plus,
  Search,
  Users,
  Video,
} from "lucide-react";
import { Button } from "src/components/ui/Button";
import { Input } from "src/components/ui/input";
import { Badge } from "src/components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "src/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "src/components/ui/sheet";
import AuthenticatedImage from "src/components/ui/AuthenticatedImage";
import { usePageHeaderStore } from "src/store/pageHeaderStore";
import {
  SessionData,
  SessionSchedulingType,
  SessionStatus,
  sessionsService,
} from "src/services/sessionsService";

type SegmentTab = "all" | "live_today" | "attention" | "completed";

const formatName = (
  profile?: { first_name?: string | null; last_name?: string | null; email?: string | null }
) => {
  const parts = [profile?.first_name, profile?.last_name]
    .filter(Boolean)
    .map((p) => String(p).trim())
    .filter((p) => p && p.toLowerCase() !== "undefined" && p.toLowerCase() !== "null");
  const name = parts.join(" ").trim();
  return name || profile?.email || "Unassigned";
};

const getInitials = (name?: string | null) => {
  if (!name || typeof name !== "string") return "RF";
  const words = name
    .trim()
    .split(/\s+/)
    .map((w) => w.trim())
    .filter((w) => Boolean(w) && w.toLowerCase() !== "undefined" && w.toLowerCase() !== "null");

  if (words.length === 0) return "RF";
  if (words.length === 1) {
    return words[0].slice(0, 2).toUpperCase();
  }
  const first = words[0].charAt(0);
  const last = words[words.length - 1].charAt(0);
  return `${first}${last}`.toUpperCase();
};

const formatDateOnly = (value: string) => {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
  }).format(date);
};

const formatTimeOnly = (value: string) => {
  const date = new Date(value);
  return new Intl.DateTimeFormat(undefined, {
    hour: "numeric",
    minute: "2-digit",
  }).format(date);
};

const isSameDay = (date1: Date, date2: Date) =>
  date1.getFullYear() === date2.getFullYear() &&
  date1.getMonth() === date2.getMonth() &&
  date1.getDate() === date2.getDate();

const getSessionTitle = (session: SessionData) =>
  session.curriculum_objective?.title ||
  session.curriculum_unit?.name ||
  session.topic?.name ||
  session.batch?.product_name ||
  session.batch?.name ||
  "Tutoring Session";

const getSubjectName = (session: SessionData) =>
  session.curriculum_unit?.domain?.subject?.name ||
  session.topic?.subject?.name ||
  session.curriculum_unit?.domain?.name ||
  null;

const statusBadgeConfig: Record<SessionStatus, { label: string; className: string }> = {
  scheduled: {
    label: "Scheduled",
    className: "bg-blue-50 text-blue-700 border-blue-200",
  },
  started: {
    label: "Started",
    className: "bg-emerald-50 text-emerald-700 border-emerald-200",
  },
  ended: {
    label: "Ended",
    className: "bg-slate-100 text-slate-700 border-slate-200",
  },
  cancelled: {
    label: "Cancelled",
    className: "bg-red-50 text-red-700 border-red-200",
  },
  failed: {
    label: "Failed",
    className: "bg-gray-100 text-gray-700 border-gray-200",
  },
};

const schedulingTypeLabel: Record<SessionSchedulingType, string> = {
  on_demand: "On-demand",
  recurring: "Recurring",
};

const SessionsList = () => {
  const navigate = useNavigate();
  const { setTitle, setDescription, setActions, reset } = usePageHeaderStore();

  const [activeTab, setActiveTab] = useState<SegmentTab>("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [typeFilter, setTypeFilter] = useState<SessionSchedulingType | "all">("all");
  const [selectedSession, setSelectedSession] = useState<SessionData | null>(null);
  const [copiedLink, setCopiedLink] = useState(false);

  const {
    data: sessions = [],
    isLoading,
    isError,
    error,
    refetch,
  } = useQuery({
    queryKey: ["admin", "sessions", typeFilter],
    queryFn: () =>
      sessionsService.fetchAdminSessions({
        scheduling_type: typeFilter === "all" ? undefined : typeFilter,
        limit: 200,
      }),
    staleTime: 60 * 1000,
  });

  useEffect(() => {
    setTitle("All Sessions");
    setDescription("View, triage, and manage scheduled tutoring sessions");
    setActions(
      <Button
        variant="outline"
        size="sm"
        onClick={() => navigate("/admin/sessions")}
        className="flex items-center gap-1.5 rounded-[10px] border-[#e2e0ea] text-[#110f1a] hover:bg-slate-50"
      >
        <ArrowLeft size={16} />
        Back to Sessions
      </Button>
    );

    return () => reset();
  }, [setTitle, setDescription, setActions, reset, navigate]);

  // Operational metrics
  const now = new Date();
  const operationalMetrics = useMemo(() => {
    const live = sessions.filter((s) => s.status === "started");
    const today = sessions.filter((s) => {
      if (!s.scheduled_at) return false;
      return isSameDay(new Date(s.scheduled_at), now) && s.status === "scheduled";
    });

    const attention = sessions.filter((s) => {
      const hasLink = Boolean(s.meeting_url || s.meeting_session?.meeting_url);
      if (hasLink || s.status !== "scheduled") return false;
      const scheduledDate = new Date(s.scheduled_at);
      const diffMs = scheduledDate.getTime() - now.getTime();
      const diffHours = diffMs / (1000 * 60 * 60);
      return diffHours >= -1 && diffHours <= 6; // Starts soon or today
    });

    const completed = sessions.filter((s) => s.status === "ended");

    return {
      live,
      today,
      attention,
      completed,
    };
  }, [sessions, now]);

  // Filtered sessions based on Tab & Search
  const filteredSessions = useMemo(() => {
    let result = sessions;

    // Segment tab filtering
    if (activeTab === "live_today") {
      result = result.filter(
        (s) =>
          s.status === "started" ||
          (s.scheduled_at && isSameDay(new Date(s.scheduled_at), now))
      );
    } else if (activeTab === "attention") {
      result = result.filter((s) => {
        const hasLink = Boolean(s.meeting_url || s.meeting_session?.meeting_url);
        return !hasLink && s.status === "scheduled";
      });
    } else if (activeTab === "completed") {
      result = result.filter((s) => s.status === "ended");
    }

    // Text search query
    const query = searchQuery.trim().toLowerCase();
    if (query) {
      result = result.filter((session) => {
        const fields = [
          getSessionTitle(session),
          getSubjectName(session),
          session.curriculum_objective?.learning_objective,
          session.status,
          session.scheduling_type,
          session.batch?.name,
          session.batch?.product_name,
          formatName(session.tutor_profile),
          formatName(session.student_profile),
          session.tutor_profile?.email,
          session.student_profile?.email,
        ];
        return fields.some((field) => field?.toLowerCase().includes(query));
      });
    }

    return result;
  }, [sessions, activeTab, searchQuery, now]);

  const handleCopyLink = (url: string) => {
    navigator.clipboard.writeText(url);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2000);
  };

  return (
    <div className="space-y-6">
      {/* 1. OPERATIONAL HEALTH KPI TRIAGE STRIP */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3.5">
        {/* Metric: Live Now */}
        <div
          onClick={() => setActiveTab("live_today")}
          className={`p-3.5 rounded-xl border transition-all cursor-pointer bg-white ${
            activeTab === "live_today"
              ? "border-[#6e59a5] ring-2 ring-[#6e59a5]/10 shadow-sm"
              : "border-[#e5e7eb] hover:border-[#6e59a5]/60 shadow-xs"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
              Active Live Now
            </span>
            <span className="w-2 h-2 rounded-full bg-emerald-500 animate-ping" />
          </div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span className="text-2xl font-bold text-[#110f1a]">
              {operationalMetrics.live.length}
            </span>
            <span className="text-xs font-semibold text-emerald-700 bg-emerald-50 px-1.5 py-0.5 rounded border border-emerald-100">
              In Progress
            </span>
          </div>
          <p className="text-[11px] text-[#8f8c9f] mt-1">Sessions active right now</p>
        </div>

        {/* Metric: Scheduled Today */}
        <div
          onClick={() => setActiveTab("live_today")}
          className={`p-3.5 rounded-xl border transition-all cursor-pointer bg-white ${
            activeTab === "live_today"
              ? "border-[#6e59a5] ring-2 ring-[#6e59a5]/10 shadow-sm"
              : "border-[#e5e7eb] hover:border-[#6e59a5]/60 shadow-xs"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
              Scheduled Today
            </span>
            <Calendar className="w-3.5 h-3.5 text-[#6e59a5]" />
          </div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span className="text-2xl font-bold text-[#110f1a]">
              {operationalMetrics.today.length}
            </span>
            <span className="text-xs text-[#646077]">sessions</span>
          </div>
          <p className="text-[11px] text-[#8f8c9f] mt-1">Ready for delivery today</p>
        </div>

        {/* Metric: Needs Attention (Missing Teams Link) */}
        <div
          onClick={() => setActiveTab("attention")}
          className={`p-3.5 rounded-xl border transition-all cursor-pointer bg-white ${
            operationalMetrics.attention.length > 0
              ? "border-amber-300 bg-amber-50/20"
              : "border-[#e5e7eb]"
          } ${
            activeTab === "attention"
              ? "ring-2 ring-amber-500/20 border-amber-500"
              : "hover:border-amber-400"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-amber-800 uppercase tracking-wider flex items-center gap-1">
              <AlertTriangle className="w-3.5 h-3.5 text-amber-600" />
              Needs Attention
            </span>
            {operationalMetrics.attention.length > 0 && (
              <span className="text-[11px] font-bold text-amber-700 bg-amber-100 px-1.5 py-0.5 rounded">
                Action Req.
              </span>
            )}
          </div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span className="text-2xl font-bold text-amber-900">
              {operationalMetrics.attention.length}
            </span>
            <span className="text-xs text-amber-800 font-medium">Missing Link</span>
          </div>
          <p className="text-[11px] text-amber-700 mt-1">Sessions starting soon</p>
        </div>

        {/* Metric: Completed */}
        <div
          onClick={() => setActiveTab("completed")}
          className={`p-3.5 rounded-xl border transition-all cursor-pointer bg-white ${
            activeTab === "completed"
              ? "border-[#6e59a5] ring-2 ring-[#6e59a5]/10 shadow-sm"
              : "border-[#e5e7eb] hover:border-[#6e59a5]/60 shadow-xs"
          }`}
        >
          <div className="flex items-center justify-between">
            <span className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
              Completed Delivery
            </span>
            <CheckCircle2 className="w-3.5 h-3.5 text-emerald-600" />
          </div>
          <div className="flex items-baseline gap-2 mt-1.5">
            <span className="text-2xl font-bold text-[#110f1a]">
              {operationalMetrics.completed.length}
            </span>
            <span className="text-xs text-[#646077]">sessions</span>
          </div>
          <p className="text-[11px] text-[#8f8c9f] mt-1">Archived session logs</p>
        </div>
      </div>

      {/* 2. MAIN CONSOLE CARD (SEGMENT TABS + SEARCH + DATA TABLE) */}
      <div className="bg-white rounded-xl border border-[#e5e7eb] shadow-xs overflow-hidden">
        {/* Segmented Navigation Tab Header */}
        <div className="p-3.5 border-b border-[#e5e7eb] flex flex-col md:flex-row md:items-center justify-between gap-3 bg-[#faf9fe]">
          {/* Segmented Control */}
          <div className="flex items-center bg-[#eae8f4] p-1 rounded-xl w-fit">
            <button
              onClick={() => setActiveTab("all")}
              className={`px-3.5 py-1.5 rounded-lg text-xs transition-all ${
                activeTab === "all"
                  ? "bg-white text-[#110f1a] font-semibold shadow-xs"
                  : "text-[#646077] font-medium hover:text-[#110f1a]"
              }`}
            >
              All Sessions <span className="ml-1 text-[11px] opacity-75">({sessions.length})</span>
            </button>

            <button
              onClick={() => setActiveTab("live_today")}
              className={`px-3.5 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 ${
                activeTab === "live_today"
                  ? "bg-white text-[#110f1a] font-semibold shadow-xs"
                  : "text-[#646077] font-medium hover:text-[#110f1a]"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-emerald-500 animate-pulse" />
              Live & Today{" "}
              <span className="text-[11px] opacity-75">
                ({operationalMetrics.live.length + operationalMetrics.today.length})
              </span>
            </button>

            <button
              onClick={() => setActiveTab("attention")}
              className={`px-3.5 py-1.5 rounded-lg text-xs transition-all flex items-center gap-1.5 ${
                activeTab === "attention"
                  ? "bg-white text-amber-900 font-semibold shadow-xs"
                  : "text-amber-800 font-medium hover:text-amber-900"
              }`}
            >
              <span className="w-1.5 h-1.5 rounded-full bg-amber-500" />
              Needs Attention{" "}
              <span className="px-1.5 py-0.2 rounded-full bg-amber-200 text-amber-900 font-bold text-[10px]">
                {operationalMetrics.attention.length}
              </span>
            </button>

            <button
              onClick={() => setActiveTab("completed")}
              className={`px-3.5 py-1.5 rounded-lg text-xs transition-all ${
                activeTab === "completed"
                  ? "bg-white text-[#110f1a] font-semibold shadow-xs"
                  : "text-[#646077] font-medium hover:text-[#110f1a]"
              }`}
            >
              Completed{" "}
              <span className="ml-1 text-[11px] opacity-75">
                ({operationalMetrics.completed.length})
              </span>
            </button>
          </div>

          {/* Quick Schedule Button */}
          <Button
            size="sm"
            onClick={() => navigate("/admin/sessions/schedule")}
            className="rounded-[10px] bg-[#a78bfa] text-white hover:bg-[#906ee6] text-xs font-semibold flex items-center gap-1.5 shadow-xs"
          >
            <Plus size={14} />
            Schedule New Session
          </Button>
        </div>

        {/* Filter and Search Bar */}
        <div className="px-4 py-3 border-b border-[#f0f1f5] flex flex-col sm:flex-row items-center justify-between gap-3 bg-white">
          <div className="relative w-full sm:w-80">
            <Search className="w-3.5 h-3.5 text-slate-400 absolute left-3 top-2.5" />
            <Input
              type="text"
              placeholder="Search topic, tutor, student, or batch..."
              className="pl-8 text-xs bg-[#fbfbfe] border-[#dcd9e8] rounded-[10px] h-9"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
          </div>

          <div className="flex items-center gap-2 w-full sm:w-auto justify-end">
            <Select
              value={typeFilter}
              onValueChange={(val) => setTypeFilter(val as SessionSchedulingType | "all")}
            >
              <SelectTrigger className="w-full sm:w-[170px] text-xs h-9 rounded-[10px] bg-white border-[#dcd9e8]">
                <SelectValue placeholder="Scheduling Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="on_demand">On-demand</SelectItem>
                <SelectItem value="recurring">Recurring</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Error Alert */}
        {isError && (
          <div className="m-4 rounded-lg border border-red-200 bg-red-50 p-3 text-xs text-red-700 flex items-center justify-between">
            <span>Unable to load sessions: {error instanceof Error ? error.message : "Please try again."}</span>
            <Button
              variant="outline"
              size="sm"
              className="h-7 text-xs border-red-300 text-red-700 hover:bg-red-100"
              onClick={() => refetch()}
            >
              Retry
            </Button>
          </div>
        )}

        {/* Table View */}
        <div className="overflow-x-auto">
          <Table className="text-xs">
            <TableHeader>
              <TableRow className="bg-[#f8f9fa] border-b border-[#e5e7eb] text-[#646077] font-semibold uppercase tracking-wider text-[11px]">
                <TableHead className="py-3 px-4">Session & Objective</TableHead>
                <TableHead className="py-3 px-4">Timing & Duration</TableHead>
                <TableHead className="py-3 px-4">Tutor</TableHead>
                <TableHead className="py-3 px-4">Student / Batch</TableHead>
                <TableHead className="py-3 px-4">Teams Video</TableHead>
                <TableHead className="py-3 px-4">Status</TableHead>
                <TableHead className="py-3 px-4 text-right">Quick Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {isLoading ? (
                <TableRow>
                  <TableCell colSpan={7} className="py-12 text-center text-gray-500">
                    <Loader2 className="mr-2 inline h-4 w-4 animate-spin text-[#6e59a5]" />
                    Loading session operations...
                  </TableCell>
                </TableRow>
              ) : filteredSessions.length > 0 ? (
                filteredSessions.map((session) => {
                  const subjectName = getSubjectName(session);
                  const hasTeamsUrl = Boolean(
                    session.meeting_url || session.meeting_session?.meeting_url
                  );
                  const isLive = session.status === "started";
                  const tutorName = formatName(session.tutor_profile);
                  const studentName = formatName(session.student_profile);

                  return (
                    <TableRow
                      key={session.id}
                      onClick={() => setSelectedSession(session)}
                      className={`hover:bg-[#faf9fe] transition cursor-pointer group ${
                        isLive ? "bg-[#fbfaff]" : ""
                      }`}
                    >
                      {/* Session & Objective */}
                      <TableCell className="py-3.5 px-4">
                        <div className="flex items-start gap-2.5">
                          {isLive ? (
                            <span className="w-2 h-2 rounded-full bg-emerald-500 mt-1.5 shrink-0 animate-ping" />
                          ) : (
                            <span className="w-2 h-2 rounded-full bg-slate-300 mt-1.5 shrink-0" />
                          )}
                          <div>
                            <div className="font-bold text-[#110f1a] text-xs group-hover:text-[#6e59a5] transition">
                              {getSessionTitle(session)}
                            </div>
                            <div className="flex items-center gap-1.5 mt-1 flex-wrap">
                              {subjectName && (
                                <span className="text-[10px] px-1.5 py-0.5 rounded bg-purple-50 text-[#6e59a5] font-semibold border border-purple-100">
                                  {subjectName}
                                </span>
                              )}
                              <span className="text-[10px] text-[#8f8c9f]">
                                {schedulingTypeLabel[session.scheduling_type || "on_demand"]}
                              </span>
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Timing & Duration */}
                      <TableCell className="py-3.5 px-4">
                        <div className="font-medium text-[#110f1a]">
                          {formatDateOnly(session.scheduled_at)}, {formatTimeOnly(session.scheduled_at)}
                        </div>
                        <div className="text-[11px] text-[#8f8c9f] mt-0.5">
                          {session.duration_min || 60} mins
                          {isLive && (
                            <span className="ml-1.5 text-emerald-700 font-semibold">• Live Now</span>
                          )}
                        </div>
                      </TableCell>

                      {/* Tutor */}
                      <TableCell className="py-3.5 px-4">
                        <div className="flex items-center gap-2.5">
                          {session.tutor_profile?.profile_picture_url ? (
                            <AuthenticatedImage
                              src={session.tutor_profile.profile_picture_url}
                              alt={tutorName}
                              className="w-7 h-7 rounded-full object-cover shrink-0 border border-purple-200"
                              fallback={
                                <div className="w-7 h-7 rounded-full bg-purple-100 text-[#6e59a5] font-bold text-[11px] flex items-center justify-center shrink-0 border border-purple-200">
                                  {getInitials(tutorName)}
                                </div>
                              }
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-purple-100 text-[#6e59a5] font-bold text-[11px] flex items-center justify-center shrink-0 border border-purple-200">
                              {getInitials(tutorName)}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-[#110f1a]">{tutorName}</div>
                            <div className="text-[10px] text-[#8f8c9f]">
                              {session.tutor_profile?.email || "Tutor"}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Student / Batch */}
                      <TableCell className="py-3.5 px-4">
                        <div className="flex items-center gap-2.5">
                          {session.student_profile?.profile_picture_url ? (
                            <AuthenticatedImage
                              src={session.student_profile.profile_picture_url}
                              alt={studentName}
                              className="w-7 h-7 rounded-full object-cover shrink-0 border border-blue-200"
                              fallback={
                                <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-[11px] flex items-center justify-center shrink-0 border border-blue-200">
                                  {getInitials(studentName)}
                                </div>
                              }
                            />
                          ) : (
                            <div className="w-7 h-7 rounded-full bg-blue-100 text-blue-700 font-bold text-[11px] flex items-center justify-center shrink-0 border border-blue-200">
                              {getInitials(studentName)}
                            </div>
                          )}
                          <div>
                            <div className="font-medium text-[#110f1a]">{studentName}</div>
                            <div className="text-[10px] text-[#8f8c9f]">
                              {[session.batch?.name, session.batch?.product_name]
                                .filter(Boolean)
                                .join(" / ") || "1:1 Session"}
                            </div>
                          </div>
                        </div>
                      </TableCell>

                      {/* Teams Link Action */}
                      <TableCell
                        className="py-3.5 px-4"
                        onClick={(e) => e.stopPropagation()}
                      >
                        {hasTeamsUrl ? (
                          <a
                            href={
                              session.meeting_url ||
                              session.meeting_session?.meeting_url ||
                              "#"
                            }
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex items-center gap-1 px-2.5 py-1 rounded-[8px] bg-emerald-50 border border-emerald-200 text-emerald-700 hover:bg-emerald-100 font-semibold text-[11px] transition"
                          >
                            <Video className="w-3 h-3 text-emerald-600" />
                            {isLive ? "Join Bridge" : "Ready (Teams)"}
                          </a>
                        ) : (
                          <Badge
                            variant="outline"
                            className="border-amber-200 bg-amber-50 text-amber-700 text-[11px]"
                          >
                            <AlertTriangle className="mr-1 h-3 w-3 text-amber-600" />
                            Pending Link
                          </Badge>
                        )}
                      </TableCell>

                      {/* Status */}
                      <TableCell className="py-3.5 px-4">
                        <Badge
                          variant="outline"
                          className={
                            statusBadgeConfig[session.status]?.className ||
                            statusBadgeConfig.failed.className
                          }
                        >
                          {statusBadgeConfig[session.status]?.label || session.status}
                        </Badge>
                      </TableCell>

                      {/* Actions */}
                      <TableCell
                        className="py-3.5 px-4 text-right"
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="flex items-center justify-end gap-1.5">
                          <Button
                            variant="outline"
                            size="sm"
                            className="h-8 px-2 text-xs rounded-[10px] border-[#e2e0ea] text-[#6e59a5] hover:bg-purple-50 flex items-center gap-1"
                            onClick={() => setSelectedSession(session)}
                          >
                            <Eye className="w-3.5 h-3.5" />
                            Inspect
                          </Button>
                          <Button
                            variant="ghost"
                            size="sm"
                            className="h-8 w-8 p-0 rounded-[10px] text-slate-500 hover:text-[#6e59a5] hover:bg-slate-100"
                            onClick={() => navigate(`/admin/sessions/edit?id=${session.id}`)}
                            aria-label="Edit session"
                          >
                            <Edit className="w-3.5 h-3.5" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  );
                })
              ) : (
                <TableRow>
                  <TableCell colSpan={7} className="py-10 text-center text-gray-500">
                    <p className="font-medium text-sm">No sessions match your filter criteria.</p>
                    <p className="text-xs text-gray-400 mt-1">
                      Try adjusting the search query or selecting a different tab.
                    </p>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </div>

        {/* Footer / Row Count */}
        <div className="p-3.5 border-t border-[#e5e7eb] bg-[#fafafa] flex items-center justify-between text-xs text-[#646077]">
          <div>
            Showing <strong className="text-[#110f1a]">{filteredSessions.length}</strong> of{" "}
            <strong className="text-[#110f1a]">{sessions.length}</strong> total sessions
          </div>
          <div className="text-[11px] text-[#8f8c9f]">
            Click any row to open the inspection drawer
          </div>
        </div>
      </div>

      {/* 3. CALM INSPECTION DRAWER (SLIDE-OVER SHEET) */}
      <Sheet
        open={Boolean(selectedSession)}
        onOpenChange={(open) => !open && setSelectedSession(null)}
      >
        <SheetContent
          side="right"
          className="w-full sm:max-w-lg overflow-y-auto p-0 flex flex-col justify-between border-l border-[#e5e7eb] bg-white shadow-2xl"
        >
          {selectedSession && (
            <>
              {/* Header */}
              <div className="p-5 border-b border-[#e5e7eb] bg-[#faf9fe]">
                <SheetHeader className="text-left space-y-1">
                  <div className="flex items-center gap-2">
                    <Badge
                      variant="outline"
                      className={
                        statusBadgeConfig[selectedSession.status]?.className ||
                        statusBadgeConfig.failed.className
                      }
                    >
                      {statusBadgeConfig[selectedSession.status]?.label || selectedSession.status}
                    </Badge>
                    <span className="text-[10px] font-mono text-[#8f8c9f]">
                      ID: {selectedSession.id.slice(0, 10)}...
                    </span>
                  </div>
                  <SheetTitle className="text-base font-bold text-[#110f1a]">
                    {getSessionTitle(selectedSession)}
                  </SheetTitle>
                </SheetHeader>
              </div>

              {/* Body */}
              <div className="p-6 space-y-6 flex-1 text-xs">
                {/* Meeting Bridge Direct Action Callout */}
                {selectedSession.meeting_url || selectedSession.meeting_session?.meeting_url ? (
                  <div className="p-4 rounded-xl border border-emerald-200 bg-emerald-50/40 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-emerald-600 text-white flex items-center justify-center font-bold">
                        <Video className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-bold text-[#110f1a]">Microsoft Teams Bridge Ready</div>
                        <div className="text-[11px] text-emerald-800">
                          Video conference provisioned
                        </div>
                      </div>
                    </div>
                    <div className="flex items-center gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() =>
                          handleCopyLink(
                            selectedSession.meeting_url ||
                              selectedSession.meeting_session?.meeting_url ||
                              ""
                          )
                        }
                        className="h-8 text-xs rounded-[10px] border-emerald-300 text-emerald-800 hover:bg-emerald-100"
                      >
                        <Copy className="w-3.5 h-3.5 mr-1" />
                        {copiedLink ? "Copied!" : "Copy"}
                      </Button>
                      <Button
                        size="sm"
                        asChild
                        className="h-8 text-xs rounded-[10px] bg-emerald-600 text-white hover:bg-emerald-700"
                      >
                        <a
                          href={
                            selectedSession.meeting_url ||
                            selectedSession.meeting_session?.meeting_url ||
                            "#"
                          }
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          <ExternalLink className="w-3.5 h-3.5 mr-1" />
                          Join
                        </a>
                      </Button>
                    </div>
                  </div>
                ) : (
                  <div className="p-4 rounded-xl border border-amber-300 bg-amber-50/50 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                      <div className="w-8 h-8 rounded-lg bg-amber-500 text-white flex items-center justify-center">
                        <AlertTriangle className="w-4 h-4" />
                      </div>
                      <div>
                        <div className="font-bold text-amber-900">Teams Meeting Link Pending</div>
                        <div className="text-[11px] text-amber-800">
                          Link has not yet been provisioned for this session.
                        </div>
                      </div>
                    </div>
                    <Button
                      size="sm"
                      onClick={() => navigate(`/admin/sessions/edit?id=${selectedSession.id}`)}
                      className="rounded-[10px] bg-amber-600 text-white hover:bg-amber-700 text-xs font-semibold"
                    >
                      Configure Link
                    </Button>
                  </div>
                )}

                {/* Timing & Schedule */}
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
                    Schedule & Timing
                  </h4>
                  <div className="bg-[#fbfbfe] p-3.5 rounded-xl border border-[#e5e7eb] grid grid-cols-2 gap-3">
                    <div>
                      <span className="text-[11px] text-[#8f8c9f]">Date & Time</span>
                      <div className="font-bold text-[#110f1a] text-xs mt-0.5">
                        {formatDateOnly(selectedSession.scheduled_at)},{" "}
                        {formatTimeOnly(selectedSession.scheduled_at)}
                      </div>
                      <div className="text-[11px] text-[#646077] mt-0.5">
                        Timezone: {selectedSession.tutor_profile?.timezone || "Asia/Kolkata"}
                      </div>
                    </div>
                    <div>
                      <span className="text-[11px] text-[#8f8c9f]">Allocated Duration</span>
                      <div className="font-bold text-[#110f1a] text-xs mt-0.5">
                        {selectedSession.duration_min || 60} Minutes
                      </div>
                      <div className="text-[11px] text-[#6e59a5] font-semibold mt-0.5">
                        {schedulingTypeLabel[selectedSession.scheduling_type || "on_demand"]}
                      </div>
                    </div>
                  </div>
                </div>

                {/* Instruction Pair Roster */}
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
                    Instruction Pair Roster
                  </h4>
                  <div className="bg-[#fbfbfe] rounded-xl border border-[#e5e7eb] divide-y divide-[#e5e7eb]">
                    {/* Tutor */}
                    <div className="p-3.5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {selectedSession.tutor_profile?.profile_picture_url ? (
                          <AuthenticatedImage
                            src={selectedSession.tutor_profile.profile_picture_url}
                            alt={formatName(selectedSession.tutor_profile)}
                            className="w-9 h-9 rounded-full object-cover shrink-0 border border-purple-200"
                            fallback={
                              <div className="w-9 h-9 rounded-full bg-purple-100 text-[#6e59a5] font-bold text-xs flex items-center justify-center shrink-0 border border-purple-200">
                                {getInitials(formatName(selectedSession.tutor_profile))}
                              </div>
                            }
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-purple-100 text-[#6e59a5] font-bold text-xs flex items-center justify-center shrink-0 border border-purple-200">
                            {getInitials(formatName(selectedSession.tutor_profile))}
                          </div>
                        )}
                        <div>
                          <span className="text-[10px] font-semibold text-purple-700 uppercase">
                            Assigned Tutor
                          </span>
                          <div className="font-bold text-[#110f1a] text-xs">
                            {formatName(selectedSession.tutor_profile)}
                          </div>
                          <div className="text-[10px] text-[#8f8c9f]">
                            {selectedSession.tutor_profile?.email || "No email"}
                          </div>
                        </div>
                      </div>
                      <span className="text-[10px] font-medium text-[#646077] bg-slate-100 px-2 py-0.5 rounded">
                        Faculty
                      </span>
                    </div>

                    {/* Student */}
                    <div className="p-3.5 flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {selectedSession.student_profile?.profile_picture_url ? (
                          <AuthenticatedImage
                            src={selectedSession.student_profile.profile_picture_url}
                            alt={formatName(selectedSession.student_profile)}
                            className="w-9 h-9 rounded-full object-cover shrink-0 border border-blue-200"
                            fallback={
                              <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center shrink-0 border border-blue-200">
                                {getInitials(formatName(selectedSession.student_profile))}
                              </div>
                            }
                          />
                        ) : (
                          <div className="w-9 h-9 rounded-full bg-blue-100 text-blue-700 font-bold text-xs flex items-center justify-center shrink-0 border border-blue-200">
                            {getInitials(formatName(selectedSession.student_profile))}
                          </div>
                        )}
                        <div>
                          <span className="text-[10px] font-semibold text-blue-700 uppercase">
                            Learner / Student
                          </span>
                          <div className="font-bold text-[#110f1a] text-xs">
                            {formatName(selectedSession.student_profile)}
                          </div>
                          <div className="text-[10px] text-[#8f8c9f]">
                            {selectedSession.student_profile?.email || "Enrolled student"}
                          </div>
                        </div>
                      </div>
                      <span className="text-[10px] font-medium text-slate-700 bg-slate-100 px-2 py-0.5 rounded">
                        {[selectedSession.batch?.name, selectedSession.batch?.product_name]
                          .filter(Boolean)
                          .join(" • ") || "Individual Plan"}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Curriculum & Objective Target */}
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
                    Curriculum Target & Learning Goal
                  </h4>
                  <div className="bg-[#fbfbfe] p-3.5 rounded-xl border border-[#e5e7eb] space-y-2">
                    <div className="flex items-center justify-between">
                      <span className="text-[11px] text-[#8f8c9f]">Subject / Domain</span>
                      <span className="text-xs font-bold text-[#110f1a]">
                        {getSubjectName(selectedSession) || "General"}
                      </span>
                    </div>
                    {selectedSession.curriculum_unit?.name && (
                      <div className="flex items-center justify-between">
                        <span className="text-[11px] text-[#8f8c9f]">Curriculum Unit</span>
                        <span className="text-xs text-[#110f1a]">
                          {selectedSession.curriculum_unit.name}
                        </span>
                      </div>
                    )}
                    {selectedSession.curriculum_objective?.learning_objective && (
                      <div className="pt-2 border-t border-[#e5e7eb] text-[11px] text-[#646077] leading-relaxed">
                        <strong className="text-[#110f1a]">Target Outcome:</strong>{" "}
                        {selectedSession.curriculum_objective.learning_objective}
                      </div>
                    )}
                  </div>
                </div>

                {/* Technical Diagnostics */}
                <div className="space-y-2">
                  <h4 className="text-[11px] font-bold text-[#646077] uppercase tracking-wider">
                    Diagnostics & Metadata
                  </h4>
                  <div className="bg-white p-3 rounded-xl border border-[#e5e7eb] text-[11px] font-mono text-[#646077] space-y-1">
                    <div className="flex justify-between">
                      <span>Created By:</span>
                      <span className="text-[#110f1a]">{selectedSession.created_by}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Session Mode:</span>
                      <span className="text-[#110f1a]">{selectedSession.mode}</span>
                    </div>
                    <div className="flex justify-between">
                      <span>Created At:</span>
                      <span>{formatDateOnly(selectedSession.created_at)}</span>
                    </div>
                  </div>
                </div>
              </div>

              {/* Footer */}
              <div className="p-4 border-t border-[#e5e7eb] bg-[#fafafa] flex items-center justify-between gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setSelectedSession(null)}
                  className="rounded-[10px] border-[#e2e0ea] text-[#110f1a] hover:bg-slate-50 text-xs"
                >
                  Close Drawer
                </Button>
                <div className="flex items-center gap-2">
                  <Button
                    size="sm"
                    onClick={() => navigate(`/admin/sessions/edit?id=${selectedSession.id}`)}
                    className="rounded-[10px] bg-[#a78bfa] text-white hover:bg-[#906ee6] text-xs font-semibold shadow-xs"
                  >
                    <Edit className="w-3.5 h-3.5 mr-1" />
                    Edit Session Details
                  </Button>
                </div>
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
};

export default SessionsList;

